export interface Concept {
  id: string;
  title: string;
  description: string;
  estimatedMinutes: number;
}

export interface Unit {
  id: string;
  number: number;
  title: string;
  description: string;
  color: string;
  icon: string;
  concepts: Concept[];
}

export const units: Unit[] = [
  {
    id: 'kinematics',
    number: 1,
    title: 'Kinematics',
    description: 'Describing motion using position, velocity, and acceleration',
    color: '#2196F3',
    icon: '🏃',
    concepts: [
      { id: 'position-displacement', title: 'Position & Displacement', description: 'Understanding where objects are and how far they move', estimatedMinutes: 15 },
      { id: 'velocity-speed', title: 'Velocity & Speed', description: 'Rate of change of position — vectors vs scalars', estimatedMinutes: 15 },
      { id: 'acceleration', title: 'Acceleration', description: 'Rate of change of velocity and what causes it', estimatedMinutes: 15 },
      { id: 'kinematic-equations', title: 'Kinematic Equations', description: 'The four equations that describe motion with constant acceleration', estimatedMinutes: 20 },
      { id: 'free-fall', title: 'Free Fall', description: 'Motion under gravity alone — g = 9.8 m/s²', estimatedMinutes: 15 },
      { id: 'projectile-motion', title: 'Projectile Motion', description: '2D motion combining horizontal and vertical components', estimatedMinutes: 25 },
      { id: 'motion-graphs', title: 'Motion Graphs', description: 'Reading and interpreting position, velocity, and acceleration graphs', estimatedMinutes: 20 },
    ],
  },
  {
    id: 'dynamics',
    number: 2,
    title: 'Dynamics',
    description: "Newton's Laws of Motion and forces",
    color: '#F44336',
    icon: '💪',
    concepts: [
      { id: 'newtons-first-law', title: "Newton's First Law", description: 'Inertia — objects resist changes in motion', estimatedMinutes: 15 },
      { id: 'newtons-second-law', title: "Newton's Second Law", description: 'F = ma — the relationship between force, mass, and acceleration', estimatedMinutes: 20 },
      { id: 'newtons-third-law', title: "Newton's Third Law", description: 'Action-reaction pairs — every force has an equal and opposite force', estimatedMinutes: 15 },
      { id: 'free-body-diagrams', title: 'Free Body Diagrams', description: 'Drawing and analyzing all forces acting on an object', estimatedMinutes: 20 },
      { id: 'friction', title: 'Friction', description: 'Static and kinetic friction — μs and μk', estimatedMinutes: 20 },
      { id: 'tension-normal', title: 'Tension & Normal Force', description: 'Contact forces in ropes, strings, and surfaces', estimatedMinutes: 15 },
      { id: 'inclined-planes', title: 'Inclined Planes', description: 'Forces and motion on ramps and slopes', estimatedMinutes: 20 },
    ],
  },
  {
    id: 'circular-motion',
    number: 3,
    title: 'Circular Motion & Gravitation',
    description: 'Objects moving in circles and gravitational forces',
    color: '#9C27B0',
    icon: '🌍',
    concepts: [
      { id: 'uniform-circular-motion', title: 'Uniform Circular Motion', description: 'Constant speed in a circle — centripetal acceleration', estimatedMinutes: 20 },
      { id: 'centripetal-force', title: 'Centripetal Force', description: 'The net force that keeps objects moving in circles', estimatedMinutes: 20 },
      { id: 'gravitational-force', title: 'Gravitational Force', description: "Newton's Law of Universal Gravitation", estimatedMinutes: 15 },
      { id: 'orbits', title: 'Orbits & Satellites', description: 'Circular orbits, orbital speed, and weightlessness', estimatedMinutes: 20 },
    ],
  },
  {
    id: 'energy',
    number: 4,
    title: 'Energy',
    description: 'Work, energy, and conservation of energy',
    color: '#FF9800',
    icon: '⚡',
    concepts: [
      { id: 'work', title: 'Work', description: 'W = Fd cos θ — energy transfer by forces', estimatedMinutes: 15 },
      { id: 'kinetic-energy', title: 'Kinetic Energy', description: 'Energy of motion — KE = ½mv²', estimatedMinutes: 15 },
      { id: 'potential-energy', title: 'Potential Energy', description: 'Gravitational PE and spring PE', estimatedMinutes: 20 },
      { id: 'conservation-of-energy', title: 'Conservation of Energy', description: 'Energy cannot be created or destroyed — only transformed', estimatedMinutes: 20 },
      { id: 'power', title: 'Power', description: 'Rate of doing work — P = W/t', estimatedMinutes: 10 },
      { id: 'work-energy-theorem', title: 'Work-Energy Theorem', description: 'Net work equals change in kinetic energy', estimatedMinutes: 15 },
    ],
  },
  {
    id: 'momentum',
    number: 5,
    title: 'Momentum',
    description: 'Linear momentum, impulse, and collisions',
    color: '#4CAF50',
    icon: '🎱',
    concepts: [
      { id: 'linear-momentum', title: 'Linear Momentum', description: 'p = mv — momentum as a vector quantity', estimatedMinutes: 15 },
      { id: 'impulse', title: 'Impulse', description: 'J = FΔt — change in momentum from a force', estimatedMinutes: 15 },
      { id: 'conservation-of-momentum', title: 'Conservation of Momentum', description: 'Total momentum is conserved in isolated systems', estimatedMinutes: 20 },
      { id: 'collisions', title: 'Collisions', description: 'Elastic vs inelastic collisions — when is KE conserved?', estimatedMinutes: 25 },
    ],
  },
  {
    id: 'shm',
    number: 6,
    title: 'Simple Harmonic Motion',
    description: 'Oscillations, springs, and pendulums',
    color: '#00BCD4',
    icon: '🔄',
    concepts: [
      { id: 'oscillations', title: 'Oscillations & Period', description: 'Repetitive motion — period, frequency, and amplitude', estimatedMinutes: 15 },
      { id: 'springs', title: 'Springs & Hooke\'s Law', description: 'F = -kx — restoring force in springs', estimatedMinutes: 20 },
      { id: 'pendulums', title: 'Pendulums', description: 'Simple pendulum motion and what affects the period', estimatedMinutes: 15 },
      { id: 'energy-in-shm', title: 'Energy in SHM', description: 'KE and PE exchange during oscillation', estimatedMinutes: 15 },
    ],
  },
  {
    id: 'torque',
    number: 7,
    title: 'Torque & Rotational Motion',
    description: 'Rotating objects, angular quantities, and torque',
    color: '#795548',
    icon: '🎡',
    concepts: [
      { id: 'angular-quantities', title: 'Angular Quantities', description: 'Angular displacement, velocity, and acceleration', estimatedMinutes: 20 },
      { id: 'torque', title: 'Torque', description: 'τ = rF sin θ — rotational equivalent of force', estimatedMinutes: 20 },
      { id: 'rotational-inertia', title: 'Rotational Inertia', description: 'I = Σmr² — resistance to angular acceleration', estimatedMinutes: 20 },
      { id: 'rotational-dynamics', title: "Newton's Second Law for Rotation", description: 'τ_net = Iα — torque causes angular acceleration', estimatedMinutes: 15 },
      { id: 'angular-momentum', title: 'Angular Momentum', description: 'L = Iω — conservation of angular momentum', estimatedMinutes: 20 },
    ],
  },
];

export function getUnit(unitId: string): Unit | undefined {
  return units.find(u => u.id === unitId);
}

export function getConcept(unitId: string, conceptId: string): Concept | undefined {
  const unit = getUnit(unitId);
  return unit?.concepts.find(c => c.id === conceptId);
}
